import {Fragment, useEffect, useState} from 'react'

import ContactForm from './ContactForm'
import Loader from '../../shared/Loader'
import Modal from '../../shared/Modal'
import RecoveryContacts from './RecoveryContacts'
import RecoveryIntro from './RecoveryIntro'
import RecoveryRequest from './RecoveryRequest'

import {connect} from '../../../state/hooks/tools'
import {createResource, deleteResource, readEndpoint, updateResource} from '../../../lib/asyncMethods'
import {isEmpty} from '../../../lib/tools'
import {isRecoveryOpen} from '../../../lib/recovery'
import {PENDING_WRAPS, RECOVERY_INVITES, RECOVERY_REQUEST} from '../../../state/actionTypes'

import './recovery.scss'

const INVITES = 'recovery-invites'
const PENDING = 'recovery-key-shares/pending'
const REQUESTS = 'recovery-requests'

const RecoveryController = props => {
  const [loaded, setLoaded] = useState(false)
  const [started, setStarted] = useState(false)
  const [adding, setAdding] = useState(false)
  const [handover, setHandover] = useState({})

  // The recovery is read alongside the contacts because the screens that run one need the other:
  // asking contacts to release their pieces is a list of exactly the people holding them.
  useEffect(() => {
    Promise.all([
      props.readRecovery(REQUESTS),
      props.readInvites(INVITES),
      props.readPendingWraps(PENDING)
    ]).finally(() => setLoaded(true))
  }, [])

  const toggleForm = () => {
    setAdding(!adding)
  }

  const addContact = attributes => {
    props.createInvite({endpoint: INVITES, attributes}).then(response => {
      if (response.success)
        setAdding(false)
    })
  }

  const sendInvitation = ({invite, path}) => {
    props.updateInvite({endpoint: `${INVITES}/${invite.id}/${path}`})
  }

  const removeContact = invite => {
    props.deleteInvite(`${INVITES}/${invite.id}`)
  }

  // Only the user can seal a piece to a contact, so this is a request they have to make themselves.
  // The response is a count rather than a record, which is why the lists are read again afterwards
  // instead of being updated from it.
  const finishHandover = () => {
    setHandover({})

    props.wrapShares({endpoint: PENDING}).then(response => {
      if (response.success) {
        setHandover({wrapped: response.data})

        return Promise.all([props.readInvites(INVITES), props.readPendingWraps(PENDING)])
      }

      setHandover({retry: true})
    })
  }

  const start = () => {
    setStarted(true)
    setAdding(true)
  }

  // A recovery takes the page over for as long as it runs. Naming a new contact needs the very key
  // the recovery exists to get back, so there is nothing on the contacts screens that would work.
  if (isRecoveryOpen(props.recovery)) {
    return (
      <Loader loading={!loaded}>
        <RecoveryRequest recovery={props.recovery} invites={props.invites} pendingWraps={props.pendingWraps} />
      </Loader>
    )
  }

  return (
    <Loader loading={!loaded}>
      <Fragment>
        {
          (isEmpty(props.invites) && !started) ? (
            <RecoveryIntro loading={props.loading} onStart={start} />
          ) : (
            <RecoveryContacts
              invites={props.invites}
              pendingWraps={props.pendingWraps}
              onAdd={toggleForm}
              sendInvitation={sendInvitation}
              removeContact={removeContact}
              finishHandover={finishHandover}
              retry={handover.retry}
              wrapped={handover.wrapped}
              loading={props.loading}
            />
          )
        }
        <Modal
          maxWidth='sm'
          open={adding}
          handleClose={toggleForm}
          subHeading="Someone you trust to help you back into your account. They'll be emailed an invitation when you send it."
          buttonText='Close'
          title='Add a recovery contact'>
          <ContactForm handleSubmit={addContact} loading={props.loading} />
        </Modal>
      </Fragment>
    </Loader>
  )
}

const mapStateToProps = ({recovery, pendingWraps, recoveryRequest}) => ({
  invites: recovery.recoveryInvites || [],
  // the handover response is a count rather than a record, and the reducer keeps whatever it is
  // handed until the list is read again
  pendingWraps: (pendingWraps.pendingWraps || []).filter(invite => invite?.id),
  // one recovery per user, and the read fails outright when there is none
  recovery: (recoveryRequest.recoveryRequest || [])[0] || {},
  loading: recovery.loading || pendingWraps.loading
})

const actionCreators = dispatchs => ({
  readRecovery: readEndpoint(dispatchs.recoveryRequest, RECOVERY_REQUEST),
  readInvites: readEndpoint(dispatchs.recovery, RECOVERY_INVITES),
  createInvite: createResource(dispatchs.recovery, RECOVERY_INVITES),
  updateInvite: updateResource(dispatchs.recovery, RECOVERY_INVITES),
  deleteInvite: deleteResource(dispatchs.recovery, RECOVERY_INVITES),
  readPendingWraps: readEndpoint(dispatchs.pendingWraps, PENDING_WRAPS),
  wrapShares: createResource(dispatchs.pendingWraps, PENDING_WRAPS)
})

export default connect(mapStateToProps, actionCreators)(RecoveryController)
